import React, { lazy } from "react";
import { Helmet } from "react-helmet";

import {
  applyNowButtonClicked,
  callToLearnMoreButtonClicked,
  contactUsButtonClicked,
  experienceVideoStarted,
  viewMoreStoriesButtonClicked,
} from "utils/segment";

import heroImage from "../../assets/home-hero-image.jpg";
import appImage from "../../assets/home-app-image.png";
import beAPapaPalImage from "../../assets/home-BeAPapaPal.jpg";
import getInTouchImage from "../../assets/home-GetInTouch.jpg";
import papaMemberBenefitImage from "../../assets/home-PapaMemberBenefit.jpg";
import headerImage from "../../assets/homepage-header.png";

import AppButtons from "../../components/AppButtons";
import PageHeader from "../../components/PageHeader";
import Container from "../../components/Container";
import Button from "../../components/Button";
import Row from "../../components/Row";
import Col from "../../components/Col";
import PressCarousel from "../../components/PressCarousel";

const YouTube = lazy(() => import("../../components/LazyIframe"));

const Home = () => {
  const videoCode = "9OVyD9InLdw";

  return (
    <>
      <Helmet>
        <title>
          Papa - Family On-Demand - Assistance and Socialization for Seniors
        </title>
        <meta
          name="title"
          content="Papa - Family On-Demand - Assistance and Socialization for Seniors"
        ></meta>
        <meta
          name="description"
          content="Papa offers the following senior services for people still living at home or at independent living facilities: transportation services, running errands, help around the house, teaching technology and offering an amazing social experience."
        ></meta>
        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:type" content="website"></meta>
        <meta property="og:url" content="https://www.papa.com/"></meta>
        <meta
          property="og:title"
          content="Papa - Family On-Demand - Assistance and Socialization for Seniors"
        ></meta>
        <meta
          property="og:description"
          content="Papa offers the following senior services for people still living at home or at independent living facilities: transportation services, running errands, help around the house, teaching technology and offering an amazing social experience."
        ></meta>
        <meta
          property="og:image"
          content="https://www.papa.com/papa-large.jpg"
        ></meta>
        {/* <!-- Twitter --> */}
        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content="https://www.papa.com/"></meta>
        <meta
          property="twitter:title"
          content="Papa - Family On-Demand - Assistance and Socialization for Seniors"
        ></meta>
        <meta
          property="twitter:description"
          content="Papa offers the following senior services for people still living at home or at independent living facilities: transportation services, running errands, help around the house, teaching technology and offering an amazing social experience."
        ></meta>
        <meta
          property="twitter:image"
          content="https://www.papa.com/papa-large.jpg"
        ></meta>
        <link rel="preload" as="image" href={headerImage} />
      </Helmet>

      <div className="home">
        <PageHeader>
          <Row>
            <Col md={7}>
              <h1>Family On-Demand</h1>
              <hr />
              <p>
                Papa and our Papa Pals offer a hand to help, a shoulder to lean
                on, an ear to listen—supporting older adults and families with
                how and where they want to live, at home.
              </p>
              <p>
                Across the country, health plans and employers look to Papa to
                provide vital social support to their members, for family care
                that’s like family.
              </p>

              <div className="hidden-mobile">
                <Button
                  href="/contact-us"
                  onClick={contactUsButtonClicked}
                  style={{ marginRight: "10px" }}
                >
                  GET IN TOUCH
                </Button>
                <Button to="/pals/signup" onClick={applyNowButtonClicked}>
                  BE A PAPA PAL
                </Button>
              </div>
              <div className="hidden-desktop">
                <Button
                  href="/contact-us"
                  onClick={contactUsButtonClicked}
                  style={{ marginBottom: "10px" }}
                >
                  GET IN TOUCH
                </Button>
                <Button to="/pals/signup" onClick={applyNowButtonClicked}>
                  BE A PAPA PAL
                </Button>
              </div>
            </Col>
          </Row>

          <img
            data-src={headerImage}
            alt="Papa pals with papa"
            className="page-header__image img-circle lazyload"
          />
        </PageHeader>

        <div className="papa-services">
          <Container className="text-center">
            <img
              data-src={heroImage}
              alt=""
              className="lazyload mb-30 papa-services-image"
            />
            <h2>We’re here to help.</h2>
            <p>
              Stuff to do, places to be, stories to share? Our national network
              of Papa Pals provide social support and everyday assistance—to
              help with elder care, child care, and self care.
            </p>
            <p>
              Papa and our Papa Pals represent a new category of care, to quite
              literally meet our members where they are. With true help
              unbounded by the real limitations of today’s health care system.
            </p>
            <p>
              Papa is aimed at the social determinants of health, including
              loneliness and isolation, transportation access, technology and
              health care literacy, and more.
            </p>
            <p>
              Whatever the need, Papa Pals are here to help lighten the load and
              the day.
            </p>
          </Container>
        </div>

        <div className="stories text-center">
          <Container>
            <div className="section-headings">
              <h2>Connections that matter</h2>
            </div>

            <Row>
              <Col md={{ span: 10, offset: 1 }}>
                <div className="player-wrapper mb-30 mr-auto ml-auto">
                  <YouTube
                    title={"Meet Sam"}
                    videoId={videoCode}
                    className="react-player"
                    onPlay={() => {
                      experienceVideoStarted({ experience: "papa" });
                    }}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={{ span: 6, offset: 3 }}>
                <h3>Meet Sam</h3>
                <p className="mb-30">
                  {
                    "Sam and his Papa Pal enjoy grocery shopping and other errands together. More than anything, they love to share coffee and life stories with each weekly visit."
                  }
                </p>

                <Button
                  to="/video-gallery"
                  onClick={() => {
                    viewMoreStoriesButtonClicked({
                      buttonCta: "More stories",
                      featureStory: "Meet Sam",
                    });
                  }}
                >
                  MORE STORIES
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <div className="how-papa-works">
          <Container>
            <div className="section-headings text-center">
              <h2 className="mb-20">How Papa works</h2>
              <p className="mr-auto ml-auto">
                Papa Pals do a lot of little things that add up to one big
                thing—better health outcomes. We bring members and Pals
                together, with a mobile app that makes lasting connections easy.
              </p>
            </div>

            <Row>
              <Col md={6}>
                <img
                  data-src={appImage}
                  alt="Papa Apps"
                  className="img-responsive mr-auto ml-auto mb-30 devices lazyload"
                />
              </Col>
              <Col md={6} className="flex direction-column justify-center">
                <div className="steps">
                  <p>
                    <b>Step 1.</b> Members sign up for Papa through their health
                    plan or employer.
                  </p>
                  <p>
                    <b>Step 2.</b> By app or by phone call, the member requests
                    a Pal for themself or a loved one.
                  </p>
                  <p>
                    <b>Step 3.</b> The Papa Pal arrives for a visit, ready to
                    share the first great experience of many.
                  </p>
                </div>
                <AppButtons />
              </Col>
            </Row>
          </Container>
        </div>

        <PressCarousel />

        <div className="get-started text-center">
          <Container>
            <h2 className="mr-auto ml-auto">
              Ready to get started? Connect with Papa.
            </h2>
            <Row>
              <Col md={4} className="mb-30 get-started-cta">
                <img
                  data-src={beAPapaPalImage}
                  alt=""
                  className="img-responsive lazyload mr-auto ml-auto"
                />
                <h3 className="mb-15">Want Papa for your plan?</h3>
                <Button
                  to="/contact-us"
                  onClick={() => {
                    contactUsButtonClicked({
                      buttonCta: "Want Papa for your plan?",
                      siteDivName: "GetStarted",
                    });
                  }}
                >
                  GET IN TOUCH
                </Button>
              </Col>
              <Col md={4} className="mb-30 get-started-cta">
                <img
                  data-src={getInTouchImage}
                  alt=""
                  className="img-responsive lazyload mr-auto ml-auto"
                />
                <h3 className="mb-15">Seeking rewarding work?</h3>
                <Button
                  to="/pals/signup"
                  onClick={() => {
                    applyNowButtonClicked({
                      buttonCta: "Seeking rewarding work?",
                      siteDivName: "GetStarted",
                    });
                  }}
                >
                  BE A PAPA PAL
                </Button>
              </Col>
              <Col md={4} className="mb-30 get-started-cta">
                <img
                  data-src={papaMemberBenefitImage}
                  alt=""
                  className="img-responsive lazyload mr-auto ml-auto"
                />
                <h3 className="mb-15">
                  Ready to access your Papa member benefit?
                </h3>
                <Button
                  href="tel:+1-800-348-7951"
                  onClick={() => {
                    callToLearnMoreButtonClicked({
                      buttonCta: "Ready to access your Papa member benefit",
                      siteDivName: "GetStarted",
                    });
                  }}
                >
                  CALL 1-800-348-7951
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default Home;
